import { useRef, useState } from 'react';

import { ClipboardCheck } from 'lucide-react';

import type { BoardCard as Card, FlystStage } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { Page, PageHeader, Panel } from '@/components/layout/Page';
import { EmptyState, ErrorState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useCandidateDepartments, useCountries } from '@/features/candidates/api';
import { FLYST_LABEL } from '@/features/candidates/labels';
import { useDateFormat } from '@/features/company/api';
import { cx } from '@/lib/cx';
import { useDebounced } from '@/lib/useDebounced';

import { useAdvanceFlyst, useFlystBoard, type BoardFilters } from '../api';
import { BoardCard } from '../components/BoardCard';
import { HandOffModal, type HandOff } from '../components/HandOffModal';
import { ScreeningDrawer } from '../components/ScreeningDrawer';
import styles from './FlystBoardPage.module.css';

/**
 * The Flyst screening board.
 *
 * Four columns of the same register the list page shows, asked a narrower question.
 * Nothing here decides who belongs on it. The server derives that from the candidate's
 * own columns every time, so a card cannot be on a board it no longer qualifies for.
 */
export function FlystBoardPage() {
  const dateFormat = useDateFormat();

  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('');
  const [destination, setDestination] = useState('');

  // One call, unconditionally: a hook behind a conditional is a different hook on
  // the next render.
  const debouncedSearch = useDebounced(search, 300);

  const filters: BoardFilters = {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(department ? { department_id: department } : {}),
    ...(destination ? { preferred_country_id: destination } : {}),
  };

  const board = useFlystBoard(filters);
  const advance = useAdvanceFlyst(filters);
  const departments = useCandidateDepartments();
  const countries = useCountries();

  const [refused, setRefused] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [opened, setOpened] = useState<Card | null>(null);

  /**
   * The card currently being carried, and the column under it.
   *
   * The card travels through state rather than through the drag event's own data: a
   * `dataTransfer` payload is only readable on drop, so the board could not highlight
   * a column, or refuse a pointless one, while the drag was still in the air.
   */
  const [dragged, setDragged] = useState<Card | null>(null);
  const [over, setOver] = useState<FlystStage | null>(null);

  /**
   * The same card, readable synchronously.
   *
   * `dragover` has to call `preventDefault` or the drop never fires, and that decision
   * needs to know what is being carried. Read from state it is whatever React last
   * committed, and a quick drag can raise `dragover` on the target before the render
   * that followed `dragstart` has landed, so the drop is silently refused. A ref is set
   * during the event itself and is never a frame behind.
   */
  const draggedRef = useRef<Card | null>(null);

  const startDrag = (card: Card | null) => {
    draggedRef.current = card;
    setDragged(card);
  };

  // A drop onto the column the card came from is not a move, and asking the server
  // would only earn a 409 about standing still.
  const canDrop = (stage: FlystStage) =>
    draggedRef.current !== null && draggedRef.current.flyst_stage !== stage;

  /** The card waiting on the hand-off modal, if any. */
  const [handingOff, setHandingOff] = useState<Card | null>(null);

  const move = (card: Card, stage: FlystStage, handOff?: HandOff) => {
    // Arranging the sponsor screening needs a date, and the server refuses without
    // one. Asking here means somebody is prompted rather than shown a 409 explaining
    // what they should have sent.
    if (stage === 'sponsor_screening_arranged' && !handOff) {
      setRefused(null);
      setHandingOff(card);
      return;
    }

    setRefused(null);
    setMoving(card.id);
    advance.mutate(
      {
        id: card.id,
        stage,
        ...(handOff ? { handOff } : {}),
      },
      {
        onError: (error) => {
          // The card is already back where it was; the mutation put it there. This
          // says why, because a card silently springing back is indistinguishable
          // from a bug.
          setRefused(
            error instanceof ApiError
              ? `${card.full_name}: ${error.message}`
              : `${card.full_name} could not be moved.`,
          );
        },
        onSettled: () => {
          setMoving(null);
          setHandingOff(null);
        },
      },
    );
  };

  return (
    <Page>
      <PageHeader
        title="Flyst screening"
        description="Our own screening, stage by stage, from first introduction through to arranging the sponsor screening."
      />

      <Panel>
        <div className={styles.filters}>
          <Input
            type="search"
            placeholder="Search name, email, phone or reference"
            aria-label="Search candidates"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
          />
          <Select
            aria-label="Department"
            value={department}
            onChange={(event) => {
              setDepartment(event.target.value);
            }}
            placeholder="All departments"
            options={(departments.data ?? []).map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
          <Select
            aria-label="Destination"
            value={destination}
            onChange={(event) => {
              setDestination(event.target.value);
            }}
            placeholder="All destinations"
            options={(countries.data ?? []).map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
          <span className={styles.count}>
            {board.data ? (
              <>
                <b>{board.data.total}</b> {board.data.total === 1 ? 'candidate' : 'candidates'}{' '}
                on this board
              </>
            ) : null}
          </span>
        </div>
      </Panel>

      {refused ? (
        <Alert tone="danger">
          <b>That move was not allowed.</b> {refused}
        </Alert>
      ) : null}

      {board.isPending ? (
        <div className={styles.loading}>
          <Spinner size={20} label="Loading the board" />
        </div>
      ) : board.isError ? (
        <ErrorState
          message="The screening board did not load."
          onRetry={() => {
            void board.refetch();
          }}
        />
      ) : board.data.total === 0 ? (
        <EmptyState
          icon={<ClipboardCheck size={20} />}
          title="Nobody is being screened"
          body={
            search || department || destination
              ? 'No candidate matches these filters.'
              : 'Candidates appear here as soon as they are added to the register.'
          }
        />
      ) : (
        <div className={styles.board}>
          {board.data.columns.map((column) => (
            <section
              key={column.stage}
              className={cx(styles.column, over === column.stage && styles.columnOver)}
              aria-label={FLYST_LABEL[column.stage]}
              onDragOver={(event) => {
                if (!canDrop(column.stage)) return;
                // Without this the drop never fires: refusing is the default.
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                setOver(column.stage);
              }}
              onDragLeave={(event) => {
                // Only when the pointer has left the column itself. Crossing between a
                // column's own children fires dragleave too, and clearing on those
                // makes the highlight flicker the whole way across.
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setOver(null);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                setOver(null);
                const carried = draggedRef.current;
                if (carried && canDrop(column.stage)) move(carried, column.stage);
                startDrag(null);
              }}
            >
              <header className={styles.columnHead}>
                <h2 className={styles.columnTitle}>{FLYST_LABEL[column.stage]}</h2>
                <span className={styles.columnCount}>{column.total}</span>
              </header>

              <div className={styles.cards}>
                {column.cards.length === 0 ? (
                  <p className={styles.empty}>Nothing here</p>
                ) : (
                  column.cards.map((card) => (
                    <BoardCard
                      key={card.id}
                      card={card}
                      dateFormat={dateFormat}
                      moving={moving === card.id}
                      dragging={dragged?.id === card.id}
                      onDragStart={startDrag}
                      onDragEnd={() => {
                        startDrag(null);
                        setOver(null);
                      }}
                      onOpen={setOpened}
                      onMove={move}
                    />
                  ))
                )}

                {/* The count above is of everything; this says so when they differ. */}
                {column.total > column.cards.length ? (
                  <p className={styles.more}>
                    {column.total - column.cards.length} more. Narrow the filters to see them.
                  </p>
                ) : null}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Read from the board's own data rather than re-fetched: it is the same card,
          and the drawer opening on a stale copy of what is on screen behind it would
          be worse than a moment's wait. */}
      {handingOff ? (
        <HandOffModal
          card={handingOff}
          busy={moving === handingOff.id}
          onClose={() => {
            setHandingOff(null);
          }}
          onConfirm={(handOff) => {
            move(handingOff, 'sponsor_screening_arranged', handOff);
          }}
        />
      ) : null}

      <ScreeningDrawer
        card={
          opened
            ? (board.data?.columns
                .flatMap((column) => column.cards)
                .find((c) => c.id === opened.id) ?? opened)
            : null
        }
        dateFormat={dateFormat}
        moving={moving === opened?.id}
        onClose={() => {
          setOpened(null);
        }}
        onMove={move}
      />
    </Page>
  );
}
